<script setup lang="ts">
import { getAllCustomers, getTenants, validateSiteIdUsage } from '@/services/core.service'
import { ElMessage, type FormInstance, type FormRules } from 'element-plus'
import { vMaska } from 'maska/vue'
import { computed, reactive, ref } from 'vue'
import {
  SiteDataCenterTypeOptions,
  SiteArrangementOptions,
  SiteCoolingOptions,
  SiteInfrastructureOptions,
  SiteManufacturerOptions,
  SitePowerSourceOptions,
  SitePurposeOptions,
  SiteRepairServiceOptions,
  SiteStatusOptions,
  SiteType,
  type FullSite,
  SiteLoadResourceZones,
  SiteTransmissionPricingZones,
  ISOToName,
  ISOs,
} from '@/types/core/Site'
import type { ICreateAddressForm, ICreateSiteForm, IUpdateSiteForm } from '@/types/core/forms'
import type { AdminPortalAPIError } from '@/types/shared/errors'
import type { OptionType } from 'element-plus/es/components/select-v2/src/select.types'
import type { ITenant } from '@/types/core/Tenant'
import { DEFAULT_PAGE_SIZES } from '@/types/shared/pagination'
import { useSites } from '@/composables/core/sites'
import { AllTimezones } from '@/types/shared/timezones'
import type { ICustomer } from '@/types/core/Customer'
import { getCustomerDisplayName } from '@/helpers/core/customers'

const { createSiteMutation, updateSiteMutation } = useSites()

const ownedByCustomer = ref<boolean>(false)

const props = defineProps<{
  site?: FullSite
}>()

const powerSourceValues = ref<string[]>([])

const siteTypeOptions = [
  {
    key: SiteType.RepairCenter,
    value: SiteType.RepairCenter.valueOf(),
  },
  {
    key: SiteType.DataCenter,
    value: SiteType.DataCenter.valueOf(),
  },
  {
    key: SiteType.Other,
    value: SiteType.Other.valueOf(),
  },
]

const tenantSearchLoading = ref<boolean>(false)
const tenantOptions = ref<OptionType[]>([])

const customerSearchLoading = ref<boolean>(false)
const customerOptions = ref<OptionType[]>([])

const model = defineModel<boolean>()
const activeCollpaseItems = ref<string[]>([])
const operationError = ref<AdminPortalAPIError>()

const addressFormRef = ref<FormInstance>()
const addressForm = reactive<ICreateAddressForm>({
  address_1: '',
  address_2: '',
  city: '',
  state: '',
  zip: '',
  country: '',
})

const siteFormRef = ref<FormInstance>()
const siteForm = reactive<ICreateSiteForm>({
  site_id: '',
  site_name: '',
  verified: false,
  address: addressForm,
  type: undefined,
  lat: undefined,
  lon: undefined,
  parcel_id: undefined,
  owner: undefined,
  acres: undefined,
  arrangement: undefined,
  operator_tenant_id: undefined,
  operator_company: undefined,
  operator_name: undefined,
  operator_phone: undefined,
  operator_email: undefined,
  owner_tenant_id: undefined,
  owner_company: undefined,
  owner_name: undefined,
  owner_phone: undefined,
  owner_email: undefined,
  owner_customer_id: undefined,
  status: undefined,
  notes: undefined,
  timezone: undefined,

  // Data Center specific fields
  infrastructure: undefined,
  cooling_method: undefined,
  purpose: undefined,
  total_capacity: undefined,
  operational_capacity: undefined,
  power_source: undefined,
  transmission_provider: undefined,
  power_provider: undefined,
  internet_service_provider: undefined,
  uptime: undefined,
  iso: undefined,
  load_resource_zone: undefined,
  transmission_pricing_zone: undefined,
  commercial_pricing_node: undefined,

  // Repair Center specific fields
  services: undefined,
  manufacturers: undefined,
  oem_repairs: undefined,
})

const siteFormRules = reactive<FormRules<ICreateSiteForm>>({
  site_id: [
    { required: true, message: 'Site is required.' },
    { min: 5, message: 'Site ID must be at least 5 characters long.' },
    {
      asyncValidator: async (rule: any, value: any, callback: any) => {
        // If we are updating the site, this validation can be ignored
        if (props.site) {
          return callback()
        }

        const site_id = String(value)

        const inUse = await validateSiteIdUsage(site_id)
        if (inUse) {
          return callback(new Error('Site ID already in use.'))
        }

        callback()
      },
      trigger: ['blur'],
    },
  ],
  site_name: [{ required: true, message: 'Site Name is required.' }],
  timezone: [{ required: true, message: 'Timezone is required.' }],
  lat: [
    {
      type: 'pattern',
      pattern: /^(-)?\d+(\.\d{1,6})?$/,
      message: 'Latitude must be numeric with up to 6 decimals',
      trigger: 'blur',
    },
  ],
  lon: [
    {
      type: 'pattern',
      pattern: /^(-)?\d+(\.\d{1,6})?$/,
      message: 'Longitude must be numeric with up to 6 decimals',
      trigger: 'blur',
    },
  ],
  acres: [
    {
      type: 'pattern',
      pattern: /^(-)?\d+(\.\d{1,2})?$/,
      message: 'Must be numeric value with up to 2 decimals',
      trigger: 'blur',
    },
  ],
  operator_email: [{ type: 'email', message: 'A valid email is required.', trigger: 'blur' }],
  operator_phone: [
    {
      type: 'pattern',
      pattern: /^\+?\d{2,}$/,
      message: 'Phone number must be numeric.',
      trigger: 'blur',
    },
    {
      type: 'pattern',
      pattern: /^\+/,
      message: 'Phone number must include country code.',
      trigger: 'blur',
    },
  ],
  owner_email: [{ type: 'email', message: 'A valid email is required.', trigger: 'blur' }],
  owner_phone: [
    {
      type: 'pattern',
      pattern: /^\+?\d{2,}$/,
      message: 'Phone number must be numeric.',
      trigger: 'blur',
    },
    {
      type: 'pattern',
      pattern: /^\+/,
      message: 'Phone number must include country code.',
      trigger: 'blur',
    },
  ],
  owner_customer_id: [
    {
      validator: (rule, value, callback) => {
        // If ownedByCustomer is true and this field is not set, return an error
        if (ownedByCustomer.value && !value) {
          return callback(new Error('If the site is owned by a customer, a customer must be selected.'))
        }

        callback()
      },
      trigger: 'submit',
    },
  ],
  total_capacity: [
    {
      type: 'pattern',
      pattern: /^(-)?\d+(\.\d{1,3})?$/,
      message: 'Must be numeric value with up to 3 decimals',
      trigger: 'blur',
    },
  ],
  operational_capacity: [
    {
      type: 'pattern',
      pattern: /^(-)?\d+(\.\d{1,3})?$/,
      message: 'Must be numeric value with up to 3 decimals',
      trigger: 'blur',
    },
  ],
  uptime: [
    {
      type: 'pattern',
      pattern: /^(-)?\d+(\.\d{1,3})?$/,
      message: 'Must be numeric value with up to 3 decimals',
      trigger: 'blur',
    },
  ],
})

const addressFormRules = reactive<FormRules<ICreateAddressForm>>({
  address_1: [{ required: true, message: 'Address 1 is required.', trigger: 'submit' }],
  city: [{ required: true, message: 'City is required.', trigger: 'submit' }],
  state: [{ required: true, message: 'State is required.', trigger: 'submit' }],
  zip: [{ required: true, message: 'Zip is required.', trigger: 'submit' }],
  country: [{ required: true, message: 'Country is required.', trigger: 'submit' }],
})

const initForm = async () => {
  if (props.site) {
    Object.assign(siteForm, props.site)
    siteForm.owner_customer_id = props.site.owner_customer?.id
    siteForm.owner_tenant_id = props.site.owner_tenant?.id ?? undefined
    siteForm.owner_company = props.site.owner_company ?? undefined
    siteForm.owner_name = props.site.owner_name ?? undefined
    siteForm.owner_email = props.site.owner_email ?? undefined
    siteForm.owner_phone = props.site.owner_phone ?? undefined
    siteForm.operator_tenant_id = props.site.operator_tenant?.id ?? undefined
    siteForm.operator_company = props.site.operator_company ?? undefined
    siteForm.operator_name = props.site.operator_name ?? undefined
    siteForm.operator_phone = props.site.operator_phone ?? undefined
    siteForm.operator_email = props.site.operator_email ?? undefined

    Object.assign(addressForm, props.site.address)
    siteForm.address = addressForm

    powerSourceValues.value = props.site.power_source?.map((ps) => ps.type) ?? []

    if (props.site.owner_customer) {
      ownedByCustomer.value = true
      searchCustomers(props.site.owner_customer.name)
    }

    if (props.site.owner_tenant) {
      ownedByCustomer.value = false
      searchTenants(props.site.owner_tenant.company_name)
    }

    if (props.site.operator_tenant) {
      searchTenants(props.site.operator_tenant.company_name)
    }
  }
}

const isAnyAddressFieldSet = () => {
  return (
    addressForm.address_1 ||
    addressForm.address_2 ||
    addressForm.city ||
    addressForm.country ||
    addressForm.state ||
    addressForm.zip
  )
}

// A site's ISO is immutable once persisted: it drives program enrollments, whose
// programs stay tied to the ISO they were created under. Lock the field when the
// saved site already has one; it can still be set on a site that has none.
const isoLocked = computed(() => !!props.site?.iso)

const handleISOChange = () => {
  siteForm.load_resource_zone = undefined
  siteForm.transmission_pricing_zone = undefined
  siteForm.commercial_pricing_node = undefined
  siteForm.elemental_node = undefined
}

/**
 * Handles form submit.
 *
 */
const submit = async () => {
  if (!siteFormRef.value || !addressFormRef.value) {
    return
  }

  // Format power source values to objects until I figure out how to do it properly
  siteForm.power_source = powerSourceValues.value.map((ps) => {
    return {
      type: ps,
      percentage: 100,
    }
  })

  let siteFormValid: boolean = false
  let addressFormValid: boolean = false
  let validationError: boolean = false

  // Validate site form
  await siteFormRef.value.validate((valid) => {
    siteFormValid = valid
  })

  if (!siteFormValid) {
    validationError = true
    activeCollpaseItems.value.push('operatorItem')
    activeCollpaseItems.value.push('ownerItem')
    if (siteForm.type == SiteType.DataCenter.toString()) {
      activeCollpaseItems.value.push('dataCenterItem')
    }
    if (siteForm.type == SiteType.RepairCenter.toString()) {
      activeCollpaseItems.value.push('repairCenterItem')
    }
  }

  // Validate address form if any address field was set.
  // If not, mark address as null for it to be deleted
  if (isAnyAddressFieldSet()) {
    await addressFormRef.value.validate((valid) => {
      addressFormValid = valid
    })

    if (!addressFormValid) {
      validationError = true
      activeCollpaseItems.value.push('addressItem')
    }
  }

  if (validationError) {
    return
  }

  // If no address field was set, change it to null
  if (!isAnyAddressFieldSet()) {
    siteForm.address = null
  }

  try {
    if (props.site) {
      const updateForm: IUpdateSiteForm = {
        id: props.site.id!,
        ...siteForm,
      }
      await updateSiteMutation.mutateAsync(updateForm)
    } else {
      await createSiteMutation.mutateAsync(siteForm)
    }
    resetFormCloseDialog()
  } catch (err: any) {
    operationError.value = err
  }
}

/**
 * Resets form, closes dialog.
 */
const resetFormCloseDialog = () => {
  siteFormRef.value?.resetFields()
  addressFormRef.value?.resetFields()
  activeCollpaseItems.value = []
  powerSourceValues.value = []
  operationError.value = undefined
  ownedByCustomer.value = false

  model.value = false
}

/**
 * Search tenants (el-select-v2 remote method).
 *
 * @param query search query fragment.
 */
const searchTenants = async (query: string) => {
  try {
    tenantSearchLoading.value = true
    const tenants = await getTenants({
      page: 1,
      per_page: DEFAULT_PAGE_SIZES[0],
      fragment: query == '' ? undefined : query,
    })
    tenantOptions.value = tenants.data.map((tenant: ITenant) => {
      return {
        label: tenant.company_name,
        value: tenant,
      }
    })
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to search tenants.',
      grouping: true,
    })
  } finally {
    tenantSearchLoading.value = false
  }
}

const searchCustomers = async (query: string) => {
  try {
    customerSearchLoading.value = true
    const customers = await getAllCustomers({
      page: 1,
      per_page: DEFAULT_PAGE_SIZES[0],
      fragment: query == '' ? undefined : query,
    })
    customerOptions.value = customers.data.map((c: ICustomer) => {
      return {
        label: getCustomerDisplayName(c),
        value: c,
      }
    })
  } catch {
    ElMessage({
      type: 'error',
      message: 'Failed to search customers.',
      grouping: true,
    })
  } finally {
    customerSearchLoading.value = false
  }
}

/**
 * Handle operator tenant change: set the tenant name as the company name
 */
const handleOperatorTenantChange = async (value: ITenant) => {
  siteForm.operator_tenant_id = value.id
  siteForm.operator_company = value.company_name
}

/**
 * Handle owner tenant change: set the tenant name as the company name
 */
const handleOwnerTenantChange = async (value: ITenant) => {
  siteForm.owner_customer_id = undefined
  siteForm.owner_tenant_id = value.id
  siteForm.owner_company = value.company_name
}

const handleOwnerTypeChange = () => {
  siteForm.owner_customer_id = undefined
  siteForm.owner_tenant_id = undefined
  siteForm.owner_company = undefined
  siteForm.owner_name = undefined
  siteForm.owner_email = undefined
  siteForm.owner_phone = undefined
}

/**
 * Handle owner tenant change: set the tenant name as the company name
 */
const handleOwnerCustomerChange = async (customer: ICustomer) => {
  siteForm.owner_tenant_id = undefined
  siteForm.owner_customer_id = customer.id
  siteForm.owner_company = customer.company_name
  siteForm.owner_name = customer.first_name + ' ' + customer.last_name
  siteForm.owner_email = customer.email
  siteForm.owner_phone = customer.phone
}

const handleOwnerClear = () => {
  siteForm.owner_customer_id = undefined
  siteForm.owner_company = undefined
  siteForm.owner_name = undefined
  siteForm.owner_email = undefined
  siteForm.owner_phone = undefined
}

const handleOperatorClear = () => {
  siteForm.operator_tenant_id = undefined
  siteForm.operator_company = undefined
  siteForm.operator_name = undefined
  siteForm.operator_email = undefined
  siteForm.operator_phone = undefined
}
</script>

<template>
  <el-dialog
    v-model="model"
    class="upsert-site-dialog"
    :title="`${site ? 'Update' : 'Create'} Site`"
    :show-close="false"
    :width="656"
    @open="initForm"
    @closed="resetFormCloseDialog"
  >
    <el-form ref="siteFormRef" :model="siteForm" :rules="siteFormRules" label-width="230" label-position="left">
      <el-form-item label="Site ID" prop="site_id">
        <el-input v-model="siteForm.site_id" placeholder="Site ID" clearable />
      </el-form-item>
      <el-form-item label="Name" prop="site_name">
        <el-input v-model="siteForm.site_name" placeholder="Site Name" clearable />
      </el-form-item>
      <el-form-item label="Type" prop="type">
        <el-select v-model="siteForm.type" placeholder="Type" clearable>
          <el-option v-for="item in siteTypeOptions" :key="item.key" :label="item.value" :value="item.value" />
        </el-select>
      </el-form-item>
      <el-form-item label="Status" prop="status">
        <el-select v-model="siteForm.status" placeholder="Status" clearable>
          <el-option v-for="item in SiteStatusOptions" :key="item" :label="item" :value="item" />
        </el-select>
      </el-form-item>
      <el-form-item label="Timezone" prop="timezone">
        <el-select v-model="siteForm.timezone" placeholder="Timezone" clearable filterable>
          <el-option v-for="item in AllTimezones" :key="item" :label="item" :value="item" />
        </el-select>
      </el-form-item>
      <el-form-item label="Verified" prop="verified">
        <el-checkbox v-model="siteForm.verified" />
      </el-form-item>
      <el-form-item label="Notes" prop="notes">
        <el-input v-model="siteForm.notes" type="textarea" placeholder="Notes" clearable />
      </el-form-item>
      <el-collapse v-model="activeCollpaseItems">
        <el-collapse-item title="Address" name="addressItem">
          <el-form
            ref="addressFormRef"
            :model="addressForm"
            :rules="addressFormRules"
            hide-required-asterisk
            label-width="230"
            label-position="left"
          >
            <el-form-item label="Address 1" prop="address_1">
              <el-input v-model="addressForm.address_1" placeholder="Address 1" clearable />
            </el-form-item>
            <el-form-item label="Address 2" prop="address_2">
              <el-input v-model="addressForm.address_2" placeholder="Address 2" clearable />
            </el-form-item>
            <el-form-item label="City" prop="city">
              <el-input v-model="addressForm.city" placeholder="City" clearable />
            </el-form-item>
            <el-form-item label="State" prop="state">
              <el-input v-model="addressForm.state" placeholder="State" clearable />
            </el-form-item>
            <el-form-item label="Zip" prop="zip">
              <el-input v-model="addressForm.zip" placeholder="Zip" clearable />
            </el-form-item>
            <el-form-item label="Country" prop="country">
              <el-input v-model="addressForm.country" placeholder="Country" clearable />
            </el-form-item>
          </el-form>
        </el-collapse-item>
        <el-collapse-item title="Land" name="landItem">
          <el-form-item label="Latitude" prop="lat">
            <el-input v-model="siteForm.lat" placeholder="44.771342" clearable />
          </el-form-item>
          <el-form-item label="Longitude" prop="lon">
            <el-input v-model="siteForm.lon" placeholder="-94.147695" clearable />
          </el-form-item>
          <el-form-item label="Parcel ID" prop="parcel_id">
            <el-input v-model="siteForm.parcel_id" placeholder="Parcel ID" clearable />
          </el-form-item>
          <el-form-item label="Owner" prop="owner">
            <el-input v-model="siteForm.owner" placeholder="Owner" clearable />
          </el-form-item>
          <el-form-item label="Acres" prop="acres">
            <el-input v-model="siteForm.acres" placeholder="Acres" clearable />
          </el-form-item>
          <el-form-item label="Arrangement" prop="arrangement">
            <el-select v-model="siteForm.arrangement" clearable placeholder="Arrangement">
              <el-option v-for="item in SiteArrangementOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
        </el-collapse-item>
        <el-collapse-item title="Owner" name="ownerItem">
          <el-switch
            v-model="ownedByCustomer"
            class="mb-4"
            active-text="Customer"
            inactive-text="Tenant"
            @change="handleOwnerTypeChange"
          />
          <el-form-item v-if="ownedByCustomer" label="Customer" prop="owner_customer_id">
            <el-select-v2
              v-model="siteForm.owner_customer_id"
              :options="customerOptions"
              remote
              :remote-method="searchCustomers"
              :loading="customerSearchLoading"
              filterable
              clearable
              placeholder="Search customer"
              value-key="id"
              @change="handleOwnerCustomerChange"
              @clear="handleOwnerClear"
            />
          </el-form-item>
          <el-form-item v-if="!ownedByCustomer" label="Tenant" prop="owner_tenant_name">
            <el-select-v2
              v-model="siteForm.owner_tenant_id"
              :options="tenantOptions"
              remote
              :remote-method="searchTenants"
              :loading="tenantSearchLoading"
              filterable
              clearable
              placeholder="Search tenant"
              value-key="id"
              @change="handleOwnerTenantChange"
              @clear="handleOwnerClear"
            />
          </el-form-item>
          <el-form-item label="Owner Company" prop="owner_company">
            <el-input
              v-model="siteForm.owner_company"
              placeholder="Owner Company"
              clearable
              :disabled="ownedByCustomer || siteForm.owner_tenant_id !== undefined"
            />
          </el-form-item>
          <el-form-item label="Owner Name" prop="owner_name">
            <el-input v-model="siteForm.owner_name" placeholder="Owner Name" clearable :disabled="ownedByCustomer" />
          </el-form-item>
          <el-form-item label="Owner Email" prop="owner_email">
            <el-input
              v-model="siteForm.owner_email"
              type="email"
              required
              placeholder="owner.email@example.com"
              clearable
              :disabled="ownedByCustomer"
            />
          </el-form-item>
          <el-form-item label="Owner Phone" prop="owner_phone">
            <div class="phone-input">
              <el-input
                v-model="siteForm.owner_phone"
                v-maska
                type="tel"
                data-maska="+9"
                data-maska-tokens="9:[0-9]:repeated"
                placeholder="+14849994949"
                clearable
                :disabled="ownedByCustomer"
              />
            </div>
          </el-form-item>
        </el-collapse-item>
        <el-collapse-item title="Operator" name="operatorItem">
          <el-form-item label="Tenant" prop="operator_tenant_name">
            <el-select-v2
              v-model="siteForm.operator_tenant_id"
              :options="tenantOptions"
              remote
              :remote-method="searchTenants"
              :loading="tenantSearchLoading"
              filterable
              clearable
              placeholder="Search tenant"
              value-key="id"
              @change="handleOperatorTenantChange"
              @clear="handleOperatorClear"
            />
          </el-form-item>
          <el-form-item label="Operator Company" prop="operator_company">
            <el-input v-model="siteForm.operator_company" placeholder="Operator Company" clearable />
          </el-form-item>
          <el-form-item label="Operator Name" prop="operator_name">
            <el-input v-model="siteForm.operator_name" placeholder="Operator Name" clearable />
          </el-form-item>
          <el-form-item label="Operator Email" prop="operator_email">
            <el-input
              v-model="siteForm.operator_email"
              type="email"
              required
              placeholder="operator.email@example.com"
            />
          </el-form-item>
          <el-form-item label="Operator Phone" prop="operator_phone">
            <div class="phone-input">
              <el-input
                v-model="siteForm.operator_phone"
                v-maska
                type="tel"
                data-maska="+9"
                data-maska-tokens="9:[0-9]:repeated"
                placeholder="+14849994949"
                clearable
              />
            </div>
          </el-form-item>
        </el-collapse-item>
        <el-collapse-item v-show="siteForm.type == SiteType.DataCenter" title="Specifications" name="dataCenterItem">
          <el-form-item label="Type" prop="data_center_type">
            <el-select v-model="siteForm.data_center_type" clearable placeholder="Type">
              <el-option v-for="item in SiteDataCenterTypeOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="Infrastructure" prop="infrastructure">
            <el-select v-model="siteForm.infrastructure" multiple filterable clearable placeholder="Infrastructure">
              <el-option v-for="item in SiteInfrastructureOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="Cooling Method" prop="cooling_method">
            <el-select v-model="siteForm.cooling_method" multiple filterable clearable placeholder="Cooling methods">
              <el-option v-for="item in SiteCoolingOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="Purpose" prop="purpose">
            <el-select v-model="siteForm.purpose" multiple filterable clearable placeholder="Purpose">
              <el-option v-for="item in SitePurposeOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="Operational Capacity (MW)" prop="operational_capacity">
            <el-input v-model="siteForm.operational_capacity" clearable />
          </el-form-item>
          <el-form-item label="Total Capacity (MW)" prop="total_capacity">
            <el-input v-model="siteForm.total_capacity" clearable />
          </el-form-item>
          <el-form-item label="Power Source" prop="power_source">
            <el-select v-model="powerSourceValues" multiple filterable clearable placeholder="Power sources">
              <el-option v-for="item in SitePowerSourceOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="Transmission Provider" prop="transmission_provider">
            <el-input v-model="siteForm.transmission_provider" placeholder="Transmission Provider" clearable />
          </el-form-item>
          <el-form-item label="Power Provider" prop="power_provider">
            <el-input v-model="siteForm.power_provider" placeholder="Power Provider" clearable />
          </el-form-item>
          <el-form-item label="Internet Service Provider" prop="internet_service_provider">
            <el-input v-model="siteForm.internet_service_provider" placeholder="Internet Service Provider" clearable />
          </el-form-item>
          <el-form-item label="Uptime" prop="uptime">
            <el-input v-model="siteForm.uptime" placeholder="Uptime" clearable />
          </el-form-item>
        </el-collapse-item>
        <el-collapse-item v-show="siteForm.type == SiteType.DataCenter" title="Energy Zones" name="energyZoneItem">
          <el-form-item label="ISO/RTO" prop="iso">
            <el-select
              v-model="siteForm.iso"
              :disabled="isoLocked"
              clearable
              filterable
              placeholder="ISO/RTO"
              @change="handleISOChange"
            >
              <el-option v-for="item in Object.values(ISOs)" :key="item" :label="ISOToName.get(item)" :value="item" />
            </el-select>
            <span v-if="isoLocked" class="text-xs text-text-tertiary">
              ISO can't be changed once set, as it's tied to the site's program enrollments.
            </span>
          </el-form-item>
          <el-form-item label="Load Resource Zone (LRZ)" prop="load_resource_zone">
            <el-select
              v-model="siteForm.load_resource_zone"
              :disabled="!siteForm.iso"
              clearable
              filterable
              allow-create
              placeholder="Load Resource Zone (LRZ)"
            >
              <el-option
                v-for="item in siteForm.iso ? SiteLoadResourceZones.get(siteForm.iso) : []"
                :key="item"
                :label="item"
                :value="item"
              />
            </el-select>
          </el-form-item>
          <el-form-item label="Transmission Pricing Zone (TPZ)" prop="transmission_pricing_zone">
            <el-select
              v-model="siteForm.transmission_pricing_zone"
              :disabled="!siteForm.iso"
              filterable
              clearable
              allow-create
              placeholder="Transmission Pricing Zone (TPZ)"
            >
              <el-option-group
                v-for="zone in siteForm.iso ? SiteTransmissionPricingZones.get(siteForm.iso) : []"
                :key="zone.name"
                :label="zone.name"
              >
                <el-option v-for="item in zone.items" :key="item" :label="item" :value="item" />
              </el-option-group>
            </el-select>
          </el-form-item>
          <el-form-item label="Commercial Pricing Node (CP)" prop="commercial_pricing_node">
            <el-input
              v-model="siteForm.commercial_pricing_node"
              :disabled="!siteForm.iso"
              placeholder="CP Node"
              clearable
            />
          </el-form-item>
          <el-form-item label="Elemental Node" prop="elemental_node">
            <el-input
              v-model="siteForm.elemental_node"
              :disabled="!siteForm.iso"
              placeholder="Elemental Node"
              clearable
            />
          </el-form-item>
        </el-collapse-item>
        <el-collapse-item
          v-show="siteForm.type == SiteType.RepairCenter"
          title="Specifications"
          name="repairCenterItem"
        >
          <el-form-item label="OEM Repairs" prop="oem_repairs">
            <el-checkbox v-model="siteForm.oem_repairs" />
          </el-form-item>
          <el-form-item label="Manufacturers" prop="manufacturers">
            <el-select
              v-model="siteForm.manufacturers"
              multiple
              allow-create
              filterable
              clearable
              placeholder="Manufacturers"
            >
              <el-option v-for="item in SiteManufacturerOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
          <el-form-item label="Services" prop="services">
            <el-select v-model="siteForm.services" multiple filterable clearable placeholder="Services">
              <el-option v-for="item in SiteRepairServiceOptions" :key="item" :label="item" :value="item" />
            </el-select>
          </el-form-item>
        </el-collapse-item>
      </el-collapse>
    </el-form>
    <el-alert v-if="operationError" type="error" :title="operationError.description">
      <ul>
        <li v-for="detail in operationError.details" :key="detail">
          {{ detail }}
        </li>
      </ul>
    </el-alert>
    <template #footer>
      <el-button @click="resetFormCloseDialog">Cancel</el-button>
      <el-button type="primary" @click="submit">Submit</el-button>
    </template>
  </el-dialog>
</template>

<style scoped lang="scss">
.phone-input {
  width: 100%;
  display: inline-flex;
  gap: var(--spacing-spacing-sm);

  .el-select {
    width: 112px;
  }
}
</style>

<!-- Unscoped: el-dialog teleports to body, so scoped selectors can't reach it.
     Element Plus paints the collapse header/content with its own blank-fill vars,
     which it declares on .el-collapse itself — so the override must sit on that
     same element (a further-out ancestor is shadowed) to clear the backgrounds and
     let the panels inherit the dialog body. Namespaced to this dialog. -->
<style lang="scss">
.upsert-site-dialog .el-collapse {
  --el-collapse-header-bg-color: transparent;
  --el-collapse-content-bg-color: transparent;
}
</style>
